import { useEffect, useState } from "react";
import AppLayout from "@/components/layout/AppLayout";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { formatMoney } from "@/lib/constants";
import { Plus, Trash2, Edit } from "lucide-react";

const EMPTY = { name: "", unit: "adet", default_price: 0 };

export default function MaterialsPage() {
  const { isAdmin } = useAuth();
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);

  const load = () => api.get("/materials").then((r) => setItems(r.data));
  useEffect(() => { load(); }, []);

  const openForm = (m) => {
    setEditing(m);
    setForm(m ? { name: m.name, unit: m.unit, default_price: m.default_price } : EMPTY);
    setOpen(true);
  };

  const save = async () => {
    if (!form.name.trim()) return toast.error("Malzeme adı zorunludur");
    try {
      if (editing) await api.put(`/materials/${editing.id}`, form);
      else await api.post("/materials", form);
      toast.success(editing ? "Malzeme güncellendi" : "Malzeme eklendi");
      setOpen(false); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Hata"); }
  };

  const remove = async (m) => {
    if (!window.confirm(`"${m.name}" silinsin mi?`)) return;
    try {
      await api.delete(`/materials/${m.id}`);
      toast.success("Silindi"); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Silinemedi"); }
  };

  return (
    <AppLayout title="Malzeme Listesi" subtitle="Çalışma kayıtlarında kullanılan malzemeler, birimleri ve varsayılan birim fiyatları"
      actions={
        <Button onClick={() => openForm(null)} data-testid="new-material-button" className="bg-emerald-700 hover:bg-emerald-800">
          <Plus className="h-4 w-4 mr-1.5" /> Yeni Malzeme
        </Button>
      }>
      <Card className="border border-slate-200 shadow-sm overflow-hidden">
        <Table data-testid="materials-table">
          <TableHeader>
            <TableRow className="bg-slate-50">
              <TableHead>Malzeme</TableHead>
              <TableHead>Birim</TableHead>
              <TableHead className="text-right">Varsayılan Birim Fiyat</TableHead>
              <TableHead className="w-24">İşlem</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {items.length === 0 && (
              <TableRow><TableCell colSpan={4} className="text-center py-10 text-slate-500">Malzeme tanımlanmadı.</TableCell></TableRow>
            )}
            {items.map((m) => (
              <TableRow key={m.id} data-testid={`material-row-${m.id}`}>
                <TableCell className="font-medium">{m.name}</TableCell>
                <TableCell className="font-mono text-sm">{m.unit}</TableCell>
                <TableCell className="text-right font-mono">{formatMoney(m.default_price)}</TableCell>
                <TableCell>
                  <div className="flex gap-1">
                    <Button variant="ghost" size="icon" onClick={() => openForm(m)} data-testid={`edit-material-${m.id}`}>
                      <Edit className="h-4 w-4" />
                    </Button>
                    {isAdmin && (
                      <Button variant="ghost" size="icon" onClick={() => remove(m)} data-testid={`delete-material-${m.id}`}>
                        <Trash2 className="h-4 w-4 text-rose-600" />
                      </Button>
                    )}
                  </div>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Card>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent data-testid="material-dialog">
          <DialogHeader><DialogTitle>{editing ? "Malzemeyi Düzenle" : "Yeni Malzeme"}</DialogTitle></DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <Label>Malzeme Adı *</Label>
              <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="material-name-input" />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Birim</Label>
                <Input value={form.unit} onChange={(e) => setForm({ ...form, unit: e.target.value })}
                       placeholder="kg, m³, torba..." data-testid="material-unit-input" />
              </div>
              <div>
                <Label>Varsayılan Birim Fiyat (TL)</Label>
                <Input type="number" min="0" step="0.01" value={form.default_price}
                       onChange={(e) => setForm({ ...form, default_price: Number(e.target.value) || 0 })}
                       data-testid="material-price-input" />
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>İptal</Button>
            <Button onClick={save} className="bg-emerald-700 hover:bg-emerald-800" data-testid="material-submit-button">
              {editing ? "Güncelle" : "Ekle"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </AppLayout>
  );
}
